import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, startOfMonth, addMonths, subMonths } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Loader2,
  Plus,
  Save,
  Search,
  Sparkles,
  Target,
  Trash2,
  WalletCards
} from 'lucide-react';
import { Card } from '../ui/Card';
import { CategoryIcon } from '../ui/CategoryIcon';
import { supabase } from '../../services/supabaseClient';
import * as budgetService from '../../services/budgetService';

const currency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
});

const money = (value) => currency.format(Number(value || 0));
const numberValue = (value) => Number(value || 0);
const roundCurrency = (value) => Math.round(numberValue(value) * 100) / 100;
const DEFAULT_UNASSIGNED_CATEGORY = 'Unassigned / Wants';

const inputClass = "w-full rounded-xl border border-[#F0F0F0] bg-white px-3 py-2.5 text-sm font-black text-black outline-none transition focus:border-[#0077FF]";

const getEnvelopeTone = (percentage, balance) => {
  if (balance < 0 || percentage > 100) {
    return {
      text: 'text-[#EF4444]',
      bg: 'bg-[#FEF2F2]'
    };
  }

  if (percentage >= 80) {
    return {
      text: 'text-[#F59E0B]',
      bg: 'bg-[#FFFBEB]'
    };
  }

  return {
    text: 'text-[#10B981]',
    bg: 'bg-[#ECFDF5]'
  };
};

const inferCategoryGroup = (categoryName = '') => {
  const name = categoryName.toLowerCase();

  if (/(rent|mortgage|emi|loan|insurance|electric|water|gas|internet|phone|subscription|bill|credit card)/.test(name)) {
    return 'Bills';
  }

  if (/(grocery|groceries|food|medical|health|medicine|transport|fuel|education|school|child|home)/.test(name)) {
    return 'Needs';
  }

  return 'Wants';
};

export default function BudgetAllocationPage({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [categoryFilters] = useState(['All', 'Overspent', 'Underfunded', 'Fully Funded', 'Needs Attention', 'No Activity']);
  const [categoryGroups, setCategoryGroups] = useState(['Bills', 'Needs', 'Wants']);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({ Bills: true, Needs: true, Wants: true });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [groupRecords, setGroupRecords] = useState([]);
  const [categoryMeta, setCategoryMeta] = useState({});
  const [categoryDialog, setCategoryDialog] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', iconSlug: 'tag', group: 'Needs' });
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [budget, setBudget] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [baselineDrafts, setBaselineDrafts] = useState({});
  const [totals, setTotals] = useState({
    carryForward: 0,
    income: 0,
    spentByCategory: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError('');
    setStatus('');

    try {
      const monthStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const monthKey = monthStart.slice(0, 7);

      const [
        { data: categoryRows, error: categoryError },
        { data: transactionRows, error: transactionError },
        targetRows
      ] = await Promise.all([
        supabase
          .from('categories')
          .select('id, name, type, icon_slug, parent_id, group_id, category_kind, is_archived, sort_order')
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase
          .from('transactions')
          .select('date, credit, debit, category_id')
          .eq('user_id', user.id)
          .order('date', { ascending: false }),
        budgetService.getSpendingTargets(user.id)
      ]);

      if (categoryError) throw categoryError;
      if (transactionError) throw transactionError;

      const visibleCategoryRows = (categoryRows || []).filter(category => !category.is_archived);
      const groupRows = visibleCategoryRows.filter(
        category => category.category_kind === 'group'
      );
      const groupById = Object.fromEntries(groupRows.map(group => [group.id, group]));
      const expenseCategories = visibleCategoryRows.filter(
        category => category.type !== 'Income' && category.category_kind !== 'group' && category.category_kind !== 'subcategory'
      );
      const subcategoryRows = visibleCategoryRows.filter(
        category => category.type !== 'Income' && category.category_kind === 'subcategory'
      );

      const transactions = transactionRows || [];
      const carryForward = transactions
        .filter(transaction => transaction.date < monthStart)
        .reduce((sum, transaction) => sum + numberValue(transaction.credit) - numberValue(transaction.debit), 0);
      const monthTransactions = transactions.filter(
        transaction => transaction.date && transaction.date.startsWith(monthKey)
      );
      const income = monthTransactions.reduce(
        (sum, transaction) => sum + numberValue(transaction.credit),
        0
      );
      const spentByCategory = monthTransactions.reduce((acc, transaction) => {
        if (transaction.category_id && numberValue(transaction.debit) > 0) {
          acc[transaction.category_id] = (acc[transaction.category_id] || 0) + numberValue(transaction.debit);
        }
        return acc;
      }, {});

      const activeBudget = await budgetService.getBudgetForMonth(user.id, currentDate);
      const availableFunds = carryForward + income;

      await supabase
        .from('budgets')
        .update({ total_income: availableFunds, updated_at: new Date().toISOString() })
        .eq('id', activeBudget.id);

      const budgetCategories = await budgetService.getBudgetCategories(activeBudget.id);
      const budgetByCategory = Object.fromEntries(
        budgetCategories.map(row => [row.category_id, row])
      );
      const targetsByCategory = Object.fromEntries(
        (targetRows || []).map(row => [row.category_id, row])
      );

      const nextDrafts = {};
      const nextCategoryMeta = {};
      expenseCategories.forEach((category, index) => {
        const target = targetsByCategory[category.id];
        nextDrafts[category.id] = {
          allocation: String(numberValue(budgetByCategory[category.id]?.allocated_amount)),
          target: String(numberValue(target?.target_amount)),
          notes: budgetByCategory[category.id]?.notes || '',
          priority: budgetByCategory[category.id]?.priority_order ?? index
        };
        nextCategoryMeta[category.id] = {
          group: groupById[category.group_id]?.name || groupById[category.parent_id]?.name || target?.category_group || inferCategoryGroup(category.name),
          isSnoozed: Boolean(target?.is_snoozed),
          isDefaultUnassigned: Boolean(target?.is_default_unassigned)
            || category.name?.toLowerCase() === DEFAULT_UNASSIGNED_CATEGORY.toLowerCase()
        };
      });

      const nextGroups = Array.from(new Set([
        'Bills',
        'Needs',
        'Wants',
        ...groupRows.map(group => group.name),
        ...Object.values(nextCategoryMeta).map(meta => meta.group)
      ])).filter(Boolean);

      setCategories(expenseCategories);
      setSubcategories(subcategoryRows);
      setGroupRecords(groupRows);
      setCategoryGroups(nextGroups);
      setCategoryMeta(nextCategoryMeta);
      setBudget(activeBudget);
      setDrafts(nextDrafts);
      setBaselineDrafts(nextDrafts);
      setExpandedGroups(prev => {
        const next = {};
        nextGroups.forEach(group => {
          next[group] = prev[group] ?? true;
        });
        return next;
      });
      setTotals({ carryForward, income, spentByCategory });
    } catch (err) {
      setError(err.message || 'Could not load budget allocation data.');
    } finally {
      setLoading(false);
    }
  }, [currentDate, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableFunds = totals.carryForward + totals.income;
  const totalAllocated = useMemo(
    () => Object.values(drafts).reduce((sum, draft) => sum + numberValue(draft.allocation), 0),
    [drafts]
  );
  const totalTargets = useMemo(
    () => Object.values(drafts).reduce((sum, draft) => sum + numberValue(draft.target), 0),
    [drafts]
  );
  const totalSpent = useMemo(
    () => Object.values(totals.spentByCategory).reduce((sum, amount) => sum + numberValue(amount), 0),
    [totals.spentByCategory]
  );
  const toBeBudgeted = roundCurrency(availableFunds - totalAllocated);
  const unassignedCategory = categories.find(category => (
    categoryMeta[category.id]?.isDefaultUnassigned
    || category.name?.toLowerCase() === DEFAULT_UNASSIGNED_CATEGORY.toLowerCase()
  ));
  const unassignedAllocation = numberValue(drafts[unassignedCategory?.id]?.allocation);
  const unassignedSpent = numberValue(totals.spentByCategory[unassignedCategory?.id]);
  const backupReserve = roundCurrency(
    Math.max(0, unassignedAllocation - unassignedSpent) + Math.max(0, toBeBudgeted)
  );
  const isZeroBased = toBeBudgeted === 0;
  const isOverAllocated = toBeBudgeted < 0;
  const isAutoAssignable = toBeBudgeted > 0;
  const saveDisabled = saving || isOverAllocated;
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(drafts) !== JSON.stringify(baselineDrafts),
    [baselineDrafts, drafts]
  );

  const getCategoryBudgetState = useCallback(
    (category) => {
      const draft = drafts[category.id] || {};
      const allocated = roundCurrency(draft.allocation);
      const target = roundCurrency(draft.target);
      const spent = roundCurrency(totals.spentByCategory[category.id]);
      const balance = roundCurrency(allocated - spent);
      const spentUtilization = allocated > 0 ? (spent / allocated) * 100 : spent > 0 ? 100 : 0;
      const targetUtilization = target > 0 ? (spent / target) * 100 : 0;
      const utilization = Math.max(spentUtilization, targetUtilization);

      const isOverspent = balance < 0;
      const isUnderfunded = !categoryMeta[category.id]?.isSnoozed && target > allocated;
      const isFullyFunded = target > 0 && allocated >= target && balance >= 0;
      const isNoActivity = allocated === 0 && spent === 0 && target === 0;
      const hasMoneyAvailable = balance > 0;

      return {
        allocated,
        target,
        spent,
        balance,
        spentUtilization,
        targetUtilization,
        envelopeTone: getEnvelopeTone(utilization, balance),
        isUnderfunded,
        isOverfunded: target > 0 && allocated > target,
        isOverspent,
        isFullyFunded,
        isNoActivity,
        hasMoneyAvailable,
        isSnoozed: Boolean(categoryMeta[category.id]?.isSnoozed),
        statusLabel: isOverspent
          ? 'Overspent'
          : isUnderfunded
            ? 'Underfunded'
            : isFullyFunded
              ? 'Fully Funded'
              : isNoActivity
                ? 'No Spending'
                : hasMoneyAvailable
                  ? 'Healthy'
                  : 'Target Reached'
      };
    },
    [categoryMeta, drafts, totals.spentByCategory]
  );

  const filteredCategories = useMemo(() => (
    categories.filter(category => {
      const state = getCategoryBudgetState(category);
      const normalizedQuery = searchQuery.trim().toLowerCase();
      const matchesSearch = !normalizedQuery || [
        category.name,
        categoryMeta[category.id]?.group,
        state.statusLabel,
        category.icon_slug
      ].some(value => value?.toLowerCase().includes(normalizedQuery));

      if (!matchesSearch) return false;
      if (activeFilter === 'Overspent') return state.isOverspent;
      if (activeFilter === 'Underfunded') return state.isUnderfunded;
      if (activeFilter === 'Fully Funded') return state.isFullyFunded;
      if (activeFilter === 'Needs Attention') return state.isOverspent || state.isUnderfunded || state.isNoActivity;
      if (activeFilter === 'No Activity') return state.isNoActivity;
      if (activeFilter === 'Bills') return (categoryMeta[category.id]?.group || inferCategoryGroup(category.name)) === 'Bills';
      if (activeFilter === 'Needs') return (categoryMeta[category.id]?.group || inferCategoryGroup(category.name)) === 'Needs';
      if (activeFilter === 'Wants') return (categoryMeta[category.id]?.group || inferCategoryGroup(category.name)) === 'Wants';
      return true;
    })
  ), [activeFilter, categories, categoryMeta, getCategoryBudgetState, searchQuery]);

  const groupedCategories = useMemo(() => (
    categoryGroups
      .map(group => ({
        group,
        categories: filteredCategories.filter(category => (
          (categoryMeta[category.id]?.group || inferCategoryGroup(category.name)) === group
        ))
      }))
      .filter(section => section.categories.length > 0)
  ), [categoryGroups, categoryMeta, filteredCategories]);

  const overspentCategories = useMemo(() => (
    categories.filter(category => getCategoryBudgetState(category).isOverspent)
  ), [categories, getCategoryBudgetState]);

  const attentionCategories = useMemo(() => (
    categories.filter(category => {
      const state = getCategoryBudgetState(category);
      return state.isOverspent || state.isUnderfunded || state.isNoActivity;
    })
  ), [categories, getCategoryBudgetState]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleQuickAllocate = (categoryId, amount) => {
    const reserveAvailable = categoryId === unassignedCategory?.id
      ? 0
      : Math.max(0, roundCurrency(drafts[unassignedCategory?.id]?.allocation) - unassignedSpent);
    const availableToAssign = Math.max(0, toBeBudgeted);
    const amountToAllocate = Math.min(amount, reserveAvailable + availableToAssign);
    const movedFromReserve = Math.min(amountToAllocate, reserveAvailable);

    if (amountToAllocate === 0) {
      setError('No backup or unassigned funds are available. Reduce another category allocation to free up money.');
      setStatus('');
      return;
    }

    setDrafts(current => {
      const targetAllocation = roundCurrency(current[categoryId]?.allocation);
      const reserveAllocation = roundCurrency(current[unassignedCategory?.id]?.allocation);

      return {
        ...current,
        [categoryId]: {
          ...(current[categoryId] || {}),
          allocation: String(roundCurrency(targetAllocation + amountToAllocate))
        },
        ...(movedFromReserve > 0 && unassignedCategory ? {
          [unassignedCategory.id]: {
            ...current[unassignedCategory.id],
            allocation: String(roundCurrency(reserveAllocation - movedFromReserve))
          }
        } : {})
      };
    });
    setError('');
    setStatus('');
    setSaveState('unsaved');
  };

  const subcategoriesByParent = useMemo(() => (
    subcategories.reduce((acc, subcategory) => {
      if (!acc[subcategory.parent_id]) acc[subcategory.parent_id] = [];
      acc[subcategory.parent_id].push(subcategory);
      return acc;
    }, {})
  ), [subcategories]);

  const groupRecordByName = useMemo(() => (
    Object.fromEntries(groupRecords.map(group => [group.name, group]))
  ), [groupRecords]);

  const updateDraft = (categoryId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [categoryId]: {
        ...(prev[categoryId] || {}),
        [field]: value
      }
    }));
    setStatus('');
    setSaveState('unsaved');
  };

  const openCategoryDialog = (mode, item = null, context = {}) => {
    setError('');
    setStatus('');
    setCategoryDialog({ mode, item, context });
    setCategoryForm({
      name: item?.name || '',
      iconSlug: item?.icon_slug || 'tag',
      group: context.group || categoryMeta[item?.id]?.group || 'Needs'
    });
  };

  const closeCategoryDialog = () => {
    setCategoryDialog(null);
    setCategoryForm({ name: '', iconSlug: 'tag', group: 'Needs' });
  };

  const ensureGroupRecord = async (groupName) => {
    if (groupRecordByName[groupName]) return groupRecordByName[groupName];

    return budgetService.handleAddCategory({
      userId: user.id,
      name: groupName,
      parentId: null,
      categoryKind: 'group',
      type: 'Expense',
      iconSlug: 'folder',
      sortOrder: categoryGroups.indexOf(groupName)
    });
  };

  const handleAddCategory = async () => {
    if (!categoryDialog) return;

    const cleanName = categoryForm.name.trim();
    if (!cleanName) {
      setError('Name is required.');
      return;
    }

    setCategoryActionLoading(true);
    try {
      const { mode, context } = categoryDialog;
      let parentId = context.parentId || null;
      let categoryKind = context.categoryKind || 'category';

      if (mode === 'addGroup') {
        categoryKind = 'group';
        parentId = null;
      } else if (categoryKind === 'category') {
        const group = await ensureGroupRecord(categoryForm.group);
        parentId = group.id;
        context.groupId = group.id;
      }

      const parentCategory = categories.find(category => category.id === parentId);

      await budgetService.handleAddCategory({
        userId: user.id,
        name: cleanName,
        parentId,
        groupId: context.groupId || parentCategory?.group_id || parentCategory?.parent_id || null,
        categoryKind,
        type: 'Expense',
        iconSlug: categoryForm.iconSlug,
        sortOrder: categories.length + subcategories.length + groupRecords.length
      });

      closeCategoryDialog();
      await loadData();
      setStatus('Category hierarchy updated.');
    } catch (err) {
      setError(err.message || 'Could not add category.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleEditCategory = async () => {
    if (!categoryDialog?.item) return;

    const cleanName = categoryForm.name.trim();
    if (!cleanName) {
      setError('Name is required.');
      return;
    }

    setCategoryActionLoading(true);
    try {
      const updates = {
        name: cleanName,
        iconSlug: categoryForm.iconSlug
      };

      if (categoryDialog.item.category_kind === 'category') {
        const group = await ensureGroupRecord(categoryForm.group);
        updates.parentId = group.id;
        updates.groupId = group.id;
      }

      await budgetService.handleEditCategory(categoryDialog.item.id, updates);

      closeCategoryDialog();
      await loadData();
      setStatus('Category hierarchy updated.');
    } catch (err) {
      setError(err.message || 'Could not edit category.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!categoryDialog?.item) return;

    setCategoryActionLoading(true);
    try {
      await budgetService.handleDeleteCategory(categoryDialog.item.id);
      closeCategoryDialog();
      await loadData();
      setStatus('Category removed. Items with existing expenses are archived safely.');
    } catch (err) {
      setError(err.message || 'Could not delete category.');
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const zeroBasedError = isOverAllocated
    ? `Allocations are higher than available funds. Reduce envelopes by ${money(Math.abs(toBeBudgeted))} before saving.`
    : `Assign the remaining ${money(toBeBudgeted)} before saving this zero-based budget.`;

  const autoAssignMessage = `${money(toBeBudgeted)} will be added to your ${DEFAULT_UNASSIGNED_CATEGORY} backup reserve when you save.`;

  const ensureDefaultUnassignedCategory = async () => {
    const existing = categories.find(category => (
      categoryMeta[category.id]?.isDefaultUnassigned
      || category.name?.toLowerCase() === DEFAULT_UNASSIGNED_CATEGORY.toLowerCase()
    ));

    if (existing) return existing;

    const { data, error: createError } = await supabase
      .from('categories')
      .insert([{ user_id: user.id, name: DEFAULT_UNASSIGNED_CATEGORY, type: 'Expense', icon_slug: 'wallet' }])
      .select('id, name, type, icon_slug')
      .single();

    if (createError) {
      throw createError;
    }

    return data;
  };

  const saveAllocations = async () => {
    if (!budget?.id) {
      setError('Budget month is not ready yet. Refresh and try again.');
      return;
    }

    if (isOverAllocated) {
      setError(zeroBasedError);
      return;
    }

    setSaving(true);
    setError('');
    setStatus('');
    setSaveState('saving');

    try {
      const now = new Date().toISOString();
      const saveCategories = [...categories];
      const saveDrafts = { ...drafts };
      const saveMeta = { ...categoryMeta };
      const unassignedAmount = roundCurrency(toBeBudgeted);

      if (unassignedAmount > 0) {
        const unassignedCategory = await ensureDefaultUnassignedCategory();
        const existingDraft = saveDrafts[unassignedCategory.id] || {};

        if (!saveCategories.some(category => category.id === unassignedCategory.id)) {
          saveCategories.push(unassignedCategory);
        }

        saveDrafts[unassignedCategory.id] = {
          ...existingDraft,
          allocation: String(roundCurrency(numberValue(existingDraft.allocation) + unassignedAmount)),
          target: String(numberValue(existingDraft.target)),
          notes: existingDraft.notes || 'Auto-assigned remaining To Be Budgeted',
          priority: existingDraft.priority ?? saveCategories.length - 1
        };
        saveMeta[unassignedCategory.id] = {
          group: 'Wants',
          isSnoozed: false,
          isDefaultUnassigned: true
        };
      }

      for (const [index, category] of saveCategories.entries()) {
        const draft = saveDrafts[category.id] || {};
        const meta = saveMeta[category.id] || {};
        const allocation = roundCurrency(draft.allocation);
        const target = roundCurrency(draft.target);
        const spent = roundCurrency(totals.spentByCategory[category.id]);

        const { error: allocationError } = await supabase
          .from('budget_categories')
          .upsert(
            {
              budget_id: budget.id,
              category_id: category.id,
              allocated_amount: allocation,
              spent_amount: spent,
              priority_order: draft.priority ?? index,
              notes: draft.notes || null,
              updated_at: now
            },
            { onConflict: 'budget_id,category_id' }
          );

        if (allocationError) {
          throw allocationError;
        }

        const { error: targetError } = await supabase
          .from('category_targets')
          .upsert(
            {
              user_id: user.id,
              category_id: category.id,
              target_amount: target,
              target_type: 'spend',
              frequency: 'monthly',
              category_group: meta.group || inferCategoryGroup(category.name),
              is_snoozed: Boolean(meta.isSnoozed),
              is_default_unassigned: Boolean(meta.isDefaultUnassigned),
              updated_at: now
            },
            { onConflict: 'user_id,category_id' }
          );

        if (targetError) {
          throw targetError;
        }
      }

      const savedTotalAllocated = roundCurrency(totalAllocated + Math.max(unassignedAmount, 0));
      const { error: budgetError } = await supabase
        .from('budgets')
        .update({
          total_income: roundCurrency(availableFunds),
          total_allocated: savedTotalAllocated,
          total_spent: roundCurrency(totalSpent),
          updated_at: now
        })
        .eq('id', budget.id);

      if (budgetError) {
        throw budgetError;
      }

      await loadData();
      setStatus(unassignedAmount > 0
        ? `Budget saved. ${money(unassignedAmount)} was added to your ${DEFAULT_UNASSIGNED_CATEGORY} backup reserve.`
        : 'Budget saved. Every rupee has a job.'
      );
      setSaveState('saved');
    } catch (err) {
      setError(err.message || 'Could not save budget allocations.');
      setSaveState('failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <div className="rounded-3xl border border-[#F0F0F0] bg-white px-8 py-6 text-center">
          <Loader2 className="mx-auto mb-3 animate-spin text-[#0077FF]" size={28} />
          <p className="text-xs font-black uppercase tracking-[0.24em] text-[#71717A]">Loading budget</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 pb-28 font-sans md:pb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.28em] text-[#71717A]">Budget Workspace</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight text-black sm:text-4xl">Zero-based planning</h1>
        </div>

        <div className="flex items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-white p-2 shadow-[0_12px_30px_rgba(15,23,42,0.05)]">
          <button
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="grid h-10 w-10 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#0077FF] hover:text-[#0077FF]"
            aria-label="Previous month"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="w-[160px] text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#71717A]">Month</p>
            <p className="truncate text-sm font-black text-black">{format(currentDate, 'MMMM yyyy')}</p>
          </div>
          <button
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="grid h-10 w-10 place-items-center rounded-xl border border-[#F0F0F0] text-black transition hover:border-[#0077FF] hover:text-[#0077FF]"
            aria-label="Next month"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#EF4444] shadow-[0_10px_24px_rgba(239,68,68,0.08)]">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {status && (
        <div className="flex items-start gap-3 rounded-2xl bg-[#ECFDF5] px-4 py-3 text-sm font-bold text-[#10B981] shadow-[0_10px_24px_rgba(16,185,129,0.08)]">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <span>{status}</span>
        </div>
      )}

      <Card className="border border-[#F0F0F0] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-0 text-white shadow-[0_22px_45px_rgba(15,23,42,0.18)] md:sticky md:top-4 md:z-10">
        <div className="grid gap-5 p-5 md:grid-cols-[1.2fr_0.8fr] md:p-7">
          <div className="space-y-5">
            <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.24em] text-slate-300">
              <Sparkles size={14} /> Budget health
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-300">{isZeroBased ? 'Every rupee has a job.' : `${money(toBeBudgeted)} still available to assign.`}</p>
              <div className="flex flex-wrap items-end gap-3">
                <p className="text-4xl font-black tracking-tight sm:text-5xl">{money(availableFunds)}</p>
                <div className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-sm font-semibold text-slate-200">
                  {format(currentDate, 'MMMM yyyy')}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-slate-300 sm:grid-cols-4">
                <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Allocated</p>
                  <p className="mt-1 font-black text-white">{money(totalAllocated)}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Spent</p>
                  <p className="mt-1 font-black text-white">{money(totalSpent)}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">To assign</p>
                  <p className="mt-1 font-black text-white">{money(toBeBudgeted)}</p>
                </div>
                <div className="rounded-2xl border border-emerald-300/20 bg-emerald-300/10 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-100">Backup reserve</p>
                  <p className="mt-1 font-black text-white">{money(backupReserve)}</p>
                  <p className="mt-1 text-[10px] leading-4 text-emerald-100/80">For category top-ups or next month</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-semibold text-slate-300">
                <span>Budget completion</span>
                <span>{Math.min(100, Math.round((totalAllocated / Math.max(availableFunds, 1)) * 100))}%</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-white/10">
                <div
                  className={`h-full rounded-full ${isOverAllocated ? 'bg-rose-400' : 'bg-emerald-400'}`}
                  style={{ width: `${Math.min(100, Math.max(0, (totalAllocated / Math.max(availableFunds, 1)) * 100))}%` }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-300">Insights</p>
            <div className="mt-4 space-y-3">
              <div className="rounded-2xl bg-white/10 p-3">
                <p className="text-sm font-semibold text-white">{backupReserve > 0 ? `${money(backupReserve)} is set aside in your backup reserve. Category quick-adds use this reserve first.` : isZeroBased ? 'Everything is assigned and ready to track.' : `You have ${money(toBeBudgeted)} ready to assign or save as backup.`}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-3">
                <p className="text-sm font-semibold text-white">{overspentCategories.length > 0 ? `${overspentCategories.length} categories are already over their plan.` : 'No categories are currently overspent.'}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-3">
                <p className="text-sm font-semibold text-white">{attentionCategories.length > 0 ? `${attentionCategories.length} categories need attention this month.` : 'Your budget is in a healthy rhythm.'}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: 'Budget Complete',
            value: isZeroBased ? 'Ready' : `${money(toBeBudgeted)}`,
            detail: isZeroBased ? 'All available funds are assigned.' : 'Still waiting for a final allocation.',
            icon: CheckCircle2,
            accent: 'text-emerald-600'
          },
          {
            label: 'Overspent Categories',
            value: overspentCategories.length.toString(),
            detail: overspentCategories.length > 0 ? 'Review these first.' : 'No immediate pressure points.',
            icon: AlertTriangle,
            accent: 'text-rose-600'
          },
          {
            label: 'Remaining to Allocate',
            value: money(toBeBudgeted),
            detail: 'Use quick actions to fill the gaps.',
            icon: WalletCards,
            accent: 'text-amber-600'
          },
          {
            label: 'Monthly Goal Progress',
            value: `${Math.min(100, Math.round((totalTargets ? (totalAllocated / totalTargets) * 100 : 0)))}%`,
            detail: 'Allocation progress across planned targets.',
            icon: Target,
            accent: 'text-sky-600'
          }
        ].map(card => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className="border border-[#F0F0F0] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#71717A]">{card.label}</p>
                  <p className="mt-2 break-words text-xl font-black text-black">{card.value}</p>
                  <p className="mt-1 text-sm font-semibold text-[#71717A]">{card.detail}</p>
                </div>
                <Icon className={`shrink-0 ${card.accent}`} size={22} />
              </div>
            </Card>
          );
        })}
      </section>

      <Card className="border border-[#F0F0F0] p-0">
        <div className="flex flex-col gap-4 border-b border-[#F0F0F0] p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="min-w-0">
            <h2 className="text-xl font-black tracking-tight text-black">Category workspace</h2>
            <p className="mt-1 text-sm font-semibold text-[#71717A]">Search, filter, and fund categories without leaving the page.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="flex items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#F8F8F8] px-3 py-2 text-sm text-[#71717A]">
              <Search size={16} />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search categories"
                className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-[#A1A1AA] sm:w-44"
              />
            </label>
            <button
              type="button"
              onClick={() => openCategoryDialog('addCategory', null, { categoryKind: 'category', group: 'Needs' })}
              className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-black px-4 text-sm font-black text-white transition active:scale-[0.98]"
            >
              <Plus size={17} /> Add category
            </button>
            <button
              type="button"
              onClick={saveAllocations}
              disabled={saveDisabled}
              title={isOverAllocated ? zeroBasedError : isAutoAssignable ? autoAssignMessage : 'Save budget'}
              className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl border border-[#F0F0F0] bg-white px-4 text-sm font-black text-black transition active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-zinc-200"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Save
            </button>
          </div>
        </div>

        {(isOverAllocated || isAutoAssignable) && (
          <div className="mx-4 mt-4 space-y-2 sm:mx-5">
            {isOverAllocated && (
              <div className="rounded-2xl bg-[#FEF2F2] px-4 py-3 text-sm font-bold text-[#EF4444] shadow-[0_10px_24px_rgba(239,68,68,0.08)]">
                {zeroBasedError}
              </div>
            )}
            {isAutoAssignable && (
              <div className="rounded-2xl bg-[#FFFBEB] px-4 py-3 text-sm font-bold text-[#F59E0B] shadow-[0_10px_24px_rgba(245,158,11,0.08)]">
                {autoAssignMessage}
              </div>
            )}
          </div>
        )}

        <div className="mx-4 mt-4 flex flex-wrap gap-2 sm:mx-5">
          {categoryFilters.map(filter => {
            const active = activeFilter === filter;
            return (
              <button
                key={filter}
                type="button"
                onClick={() => setActiveFilter(filter)}
                className={`rounded-full border px-3 py-2 text-xs font-black transition ${
                  active
                    ? 'border-black bg-black text-white'
                    : 'border-[#F0F0F0] bg-white text-[#71717A] hover:border-[#0077FF] hover:text-[#0077FF]'
                }`}
              >
                {filter}
              </button>
            );
          })}
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          {groupedCategories.map(section => {
            const isExpanded = expandedGroups[section.group] ?? true;
            return (
              <div key={section.group} className="rounded-3xl border border-[#F0F0F0] bg-[#FCFCFD] p-3 sm:p-4">
                <button
                  type="button"
                  onClick={() => toggleGroup(section.group)}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl px-1 py-2 text-left"
                >
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#71717A]">{section.group}</p>
                    <p className="mt-1 text-sm font-black text-black">{section.categories.length} categories</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openCategoryDialog('addCategory', null, { categoryKind: 'category', group: section.group });
                      }}
                      className="grid h-9 w-9 place-items-center rounded-xl border border-[#F0F0F0] bg-white text-[#71717A] transition hover:border-[#0077FF] hover:text-[#0077FF]"
                      aria-label={`Add category to ${section.group}`}
                    >
                      <Plus size={16} />
                    </button>
                    <span className="text-sm font-black text-[#71717A]">{isExpanded ? '−' : '+'}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="mt-3 space-y-3">
                    {section.categories.map(category => {
                      const draft = drafts[category.id] || {};
                      const state = getCategoryBudgetState(category);
                      const childSubcategories = subcategoriesByParent[category.id] || [];
                      const isCategoryExpanded = expandedCategories[category.id] ?? false;
                      const progressPercent = Math.min(100, Math.round((state.allocated > 0 ? (state.spent / Math.max(state.allocated, 1)) * 100 : 0)));

                      return (
                        <div key={category.id} className="rounded-3xl border border-[#F0F0F0] bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex min-w-0 items-start gap-3">
                              <CategoryIcon iconSlug={category.icon_slug} className="h-11 w-11 shrink-0 bg-[#111111] text-white" size={18} />
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-base font-black text-black">{category.name}</p>
                                  <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] ${state.isOverspent ? 'border-rose-100 bg-rose-50 text-rose-700' : state.isUnderfunded ? 'border-amber-100 bg-amber-50 text-amber-700' : state.isFullyFunded ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                                    {state.statusLabel}
                                  </span>
                                </div>
                                <p className="mt-1 text-sm font-semibold text-[#71717A]">{money(state.allocated)} allocated • {money(state.spent)} spent • {money(state.balance)} left</p>
                                {categoryMeta[category.id]?.isDefaultUnassigned && (
                                  <p className="mt-1 text-xs font-semibold text-emerald-700">
                                    Backup money: quick-add to another category to use this reserve. Unspent balance carries forward into next month.
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleCategory(category.id)}
                                className="rounded-full border border-[#F0F0F0] px-3 py-2 text-xs font-black text-[#71717A] transition hover:border-[#0077FF] hover:text-[#0077FF]"
                              >
                                {isCategoryExpanded ? 'Hide details' : 'Open'}
                              </button>
                              <button
                                type="button"
                                onClick={() => openCategoryDialog('editCategory', category)}
                                className="grid h-9 w-9 place-items-center rounded-full border border-[#F0F0F0] text-[#71717A] transition hover:border-[#0077FF] hover:text-[#0077FF]"
                                aria-label={`Manage ${category.name}`}
                              >
                                <Edit3 size={16} />
                              </button>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <div className="rounded-2xl bg-[#F8F8F8] p-3">
                              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Allocated</p>
                              <p className="mt-1 text-lg font-black text-black">{money(state.allocated)}</p>
                            </div>
                            <div className="rounded-2xl bg-[#F8F8F8] p-3">
                              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Target</p>
                              <p className="mt-1 text-lg font-black text-black">{money(state.target)}</p>
                            </div>
                            <div className="rounded-2xl bg-[#F8F8F8] p-3">
                              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Remaining</p>
                              <p className="mt-1 text-lg font-black text-black">{money(state.balance)}</p>
                            </div>
                          </div>

                          <div className="mt-4 space-y-2">
                            <div className="flex items-center justify-between text-sm font-semibold text-[#71717A]">
                              <span>Funding progress</span>
                              <span>{progressPercent}%</span>
                            </div>
                            <div className="h-2.5 overflow-hidden rounded-full bg-[#F0F0F0]">
                              <div className={`h-full rounded-full ${state.isOverspent ? 'bg-rose-500' : state.isUnderfunded ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${progressPercent}%` }} />
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-2">
                            {[100, 500, 1000].map(amount => (
                              <button
                                key={amount}
                                type="button"
                                onClick={() => handleQuickAllocate(category.id, amount)}
                                className="rounded-full border border-[#F0F0F0] bg-white px-3 py-2 text-sm font-black text-black transition hover:border-[#0077FF] hover:text-[#0077FF]"
                              >
                                +{money(amount)}
                              </button>
                            ))}
                            <label className="flex min-w-[120px] items-center gap-2 rounded-full border border-[#F0F0F0] bg-[#F8F8F8] px-3 py-2 text-sm font-semibold text-[#71717A]">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="w-full bg-transparent outline-none"
                                placeholder="Custom"
                                value={draft.allocation || ''}
                                onChange={(event) => updateDraft(category.id, 'allocation', event.target.value)}
                              />
                            </label>
                          </div>

                          {isCategoryExpanded && (
                            <div className="mt-4 grid gap-4 border-t border-[#F0F0F0] pt-4 lg:grid-cols-[1.1fr_0.9fr]">
                              <div className="space-y-3">
                                <div className="rounded-2xl bg-[#F8F8F8] p-3">
                                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Target</p>
                                  <div className="mt-2 flex items-center gap-2">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      className={inputClass}
                                      value={draft.target || ''}
                                      onChange={(event) => updateDraft(category.id, 'target', event.target.value)}
                                    />
                                  </div>
                                </div>
                                {childSubcategories.length > 0 && (
                                  <div className="rounded-2xl bg-[#F8F8F8] p-3">
                                    <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Subcategories</p>
                                    <div className="mt-2 space-y-2">
                                      {childSubcategories.map(subcategory => (
                                        <div key={subcategory.id} className="flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm font-semibold text-[#71717A]">
                                          <span>{subcategory.name}</span>
                                          <button
                                            type="button"
                                            onClick={() => openCategoryDialog('editSubcategory', subcategory)}
                                            className="text-[#0077FF]"
                                          >
                                            Edit
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="space-y-3">
                                <div className="rounded-2xl bg-[#F8F8F8] p-3">
                                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Monthly trend</p>
                                  <p className="mt-2 text-sm font-semibold text-black">{state.isOverspent ? 'Spending has outpaced this category’s allocation.' : state.isUnderfunded ? 'More funding is still required to meet the target.' : 'This category is tracking comfortably.'}</p>
                                </div>
                                <div className="rounded-2xl bg-[#F8F8F8] p-3">
                                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#71717A]">Quick actions</p>
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openCategoryDialog('addSubcategory', null, { categoryKind: 'subcategory', parentId: category.id })}
                                      className="rounded-full border border-[#F0F0F0] bg-white px-3 py-2 text-sm font-black text-black"
                                    >
                                      Add subcategory
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => openCategoryDialog('editCategory', category)}
                                      className="rounded-full border border-[#F0F0F0] bg-white px-3 py-2 text-sm font-black text-black"
                                    >
                                      Edit details
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {filteredCategories.length === 0 && (
            <div className="rounded-3xl border border-dashed border-[#F0F0F0] bg-[#F8F8F8] p-10 text-center">
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#71717A]">No categories found</p>
              <p className="mt-2 text-sm font-semibold text-[#71717A]">Try a different search term or create a new category from the workspace.</p>
            </div>
          )}
        </div>
      </Card>

      <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#F0F0F0] bg-white/95 px-4 py-3 backdrop-blur md:px-6 ${hasUnsavedChanges ? 'block' : 'hidden'}`}>
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-black text-black">{saveState === 'saving' ? 'Saving changes…' : saveState === 'saved' ? 'Budget saved' : saveState === 'failed' ? 'Save failed' : 'Unsaved changes ready to save'}</p>
            <p className="text-sm font-semibold text-[#71717A]">{saveState === 'saving' ? 'Your updated allocations are being written.' : saveState === 'saved' ? 'The latest allocation updates are now stored.' : saveState === 'failed' ? 'Please try saving again.' : 'You can save at any time.'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openCategoryDialog('addCategory', null, { categoryKind: 'category', group: 'Needs' })}
              className="flex h-11 items-center justify-center rounded-2xl border border-[#F0F0F0] px-4 text-sm font-black text-black"
            >
              Add category
            </button>
            <button
              type="button"
              onClick={saveAllocations}
              disabled={saveDisabled || saving}
              className="flex h-11 items-center justify-center gap-2 rounded-2xl bg-black px-4 text-sm font-black text-white disabled:bg-zinc-300"
            >
              {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />}
              Save now
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => openCategoryDialog('addCategory', null, { categoryKind: 'category', group: 'Needs' })}
        className="fixed bottom-24 right-4 z-30 grid h-14 w-14 place-items-center rounded-full bg-black text-white shadow-[0_16px_40px_rgba(15,23,42,0.24)] md:right-6"
        aria-label="Add category"
      >
        <Plus size={22} />
      </button>

      {categoryDialog && (
        <div className="fixed inset-0 z-[80] flex items-end bg-black/30 px-4 pb-4 sm:items-center sm:justify-center sm:p-6">
          <div className="w-full max-w-md rounded-[28px] border border-[#F0F0F0] bg-white p-5 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#71717A]">
                  {categoryDialog.mode.startsWith('edit') ? 'Edit' : 'Create'}
                </p>
                <h3 className="mt-1 text-xl font-black text-black">
                  {categoryDialog.mode === 'addGroup' && 'Category Group'}
                  {categoryDialog.mode === 'addCategory' && 'Category'}
                  {categoryDialog.mode === 'addSubcategory' && 'Subcategory'}
                  {categoryDialog.mode === 'editGroup' && 'Category Group'}
                  {categoryDialog.mode === 'editCategory' && 'Category'}
                  {categoryDialog.mode === 'editSubcategory' && 'Subcategory'}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeCategoryDialog}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[#F0F0F0] text-[#71717A]"
                aria-label="Close"
              >
                x
              </button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-[11px] font-black uppercase tracking-[0.16em] text-[#71717A]">Name</span>
                <input
                  className={inputClass}
                  value={categoryForm.name}
                  onChange={(event) => setCategoryForm(prev => ({ ...prev, name: event.target.value }))}
                  autoFocus
                />
              </label>

              {categoryDialog.mode !== 'addGroup' && categoryDialog.mode !== 'editSubcategory' && categoryDialog.mode !== 'addSubcategory' && (
                <label className="block">
                  <span className="mb-1 block text-[11px] font-black uppercase tracking-[0.16em] text-[#71717A]">Group</span>
                  <select
                    className={inputClass}
                    value={categoryForm.group}
                    onChange={(event) => setCategoryForm(prev => ({ ...prev, group: event.target.value }))}
                  >
                    {categoryGroups.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </label>
              )}

              {categoryDialog.mode !== 'addGroup' && (
                <label className="block">
                  <span className="mb-1 block text-[11px] font-black uppercase tracking-[0.16em] text-[#71717A]">Icon slug</span>
                  <input
                    className={inputClass}
                    value={categoryForm.iconSlug}
                    onChange={(event) => setCategoryForm(prev => ({ ...prev, iconSlug: event.target.value }))}
                  />
                </label>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              {categoryDialog.mode.startsWith('edit') && (
                <button
                  type="button"
                  onClick={handleDeleteCategory}
                  disabled={categoryActionLoading}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl border border-rose-100 bg-rose-50 px-4 text-sm font-black text-rose-700 disabled:opacity-50"
                >
                  <Trash2 size={17} /> Delete
                </button>
              )}
              <button
                type="button"
                onClick={categoryDialog.mode.startsWith('edit') ? handleEditCategory : handleAddCategory}
                disabled={categoryActionLoading}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-black px-4 text-sm font-black text-white disabled:bg-zinc-300"
              >
                {categoryActionLoading ? <Loader2 size={17} className="animate-spin" /> : categoryDialog.mode.startsWith('edit') ? <Edit3 size={17} /> : <Plus size={17} />}
                {categoryDialog.mode.startsWith('edit') ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
